import datapoints from '../datapoints';
import * as suppressedItemIdsMapOperations from '../helpers/suppressedItemIdsMapOperations';
import markConversationsAsReadService from '../services/markConversationsAsReadService';
import { lazyAddMarkAsReadDiagnostics } from 'owa-group-readunread-diagnostics';
import { userMailInteractionAction } from 'owa-mail-actions';
import markConversationsAsReadStoreUpdate from 'owa-mail-actions/lib/triage/markConversationsAsReadStoreUpdate';
import { MailRowDataPropertyGetter, TableView } from 'owa-mail-list-store';
import getContextFolderIdForTable from 'owa-mail-list-store/lib/utils/getContextFolderIdForTable';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { InstrumentationContext } from 'owa-mail-store';
import { trace } from 'owa-trace';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import {
    getApplyConversationActionContext,
    getTableConversationRelation,
    shouldRemoveRowOnMarkReadAction,
    canTableBeOutOfSyncWithServer,
} from 'owa-mail-list-store';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import filterConversationsByIsReadValue from './filterConversationsByIsReadValue';
import RequestOptions from 'owa-service/lib/RequestOptions';
import { markItemsAsReadServiceWithErrorHandling } from './markItemsAsReadBasedOnItemIds';

export default createLegacyActionWithDatapoint(
    'markConversationsAsReadBasedOnConversationIds',
    datapoints.markConversationAsRead
)(function markConversationsAsReadBasedOnConversationIds(
    conversationIds: string[],
    tableView: TableView,
    isReadValue: boolean,
    isExplicit: boolean,
    actionSource: ActionSource,
    instrumentationContexts: InstrumentationContext[],
    rowKeys: string[]
): Promise<void> {
    const rowKeysToUpdate = filterConversationsByIsReadValue(rowKeys, tableView.id, isReadValue);
    if (rowKeysToUpdate.length == 0) {
        // Nothing to update
        return Promise.resolve();
    }

    // Update suppressed state if user explicitly marks a single selected conversation as read/unread
    if (conversationIds.length == 1 && tableView.selectedRowKeys.size == 1) {
        const conversationId = MailRowDataPropertyGetter.getRowIdString(
            rowKeysToUpdate[0],
            tableView
        );
        const selectedConversationRowRelation = getTableConversationRelation(
            [...tableView.selectedRowKeys.keys()][0],
            tableView.id
        );
        if (conversationId == selectedConversationRowRelation.id) {
            const itemIds = selectedConversationRowRelation.itemIds;

            if (isReadValue) {
                // If mark the conversation read, always remove all item ids and clear the timestamp
                suppressedItemIdsMapOperations.clear();
            } else {
                if (isExplicit) {
                    // If mark conversation as unread explicitly, set the suppressed state by adding all itemIds
                    suppressedItemIdsMapOperations.add(itemIds);
                } else {
                    trace.warn('Invalid scenario to implicitly mark conversation as unread.');
                }
            }
        }
    }

    // 1. Make instant update in the store
    markConversationsAsReadStoreUpdate(conversationIds, tableView.id, isReadValue, isExplicit);

    // Tracking the mark as read operation for the diagnostics panel
    lazyAddMarkAsReadDiagnostics.import().then(addMarkAsReadDiagnostics => {
        addMarkAsReadDiagnostics(
            tableView.tableQuery,
            false /* isActingOnAllItemsInTable */,
            isReadValue,
            actionSource,
            conversationIds
        );
    });

    const triageContext = {
        conversationIds,
    };

    if (!isReadValue) {
        userMailInteractionAction('MarkAsUnread', instrumentationContexts, triageContext);
    } else if (isExplicit) {
        userMailInteractionAction('MarkAsRead', instrumentationContexts, triageContext);
    } else {
        userMailInteractionAction('MarkAsReadImplicit', instrumentationContexts, triageContext);
    }

    // 2. Make service request
    const { requestOptions } = getMailboxInfo(tableView);

    // Perform item level operation if the table is out of sync with server and server may not have
    // knowledge of this conversation in the given context
    if (canTableBeOutOfSyncWithServer(tableView)) {
        return performItemLevelMarkReadOperation(
            tableView,
            rowKeysToUpdate,
            isReadValue,
            requestOptions
        );
    } else {
        return performConversationLevelMarkReadOperation(
            tableView,
            rowKeysToUpdate,
            isReadValue,
            isExplicit,
            requestOptions
        );
    }
});

function performItemLevelMarkReadOperation(
    tableView: TableView,
    conversationRowKeysToUpdate: string[],
    isReadValue: boolean,
    requestOptions: RequestOptions
): Promise<void> {
    const itemIdsToUpdate = [];
    conversationRowKeysToUpdate.forEach(rowKey => {
        itemIdsToUpdate.push(...MailRowDataPropertyGetter.getItemIds(rowKey, tableView));
    });

    return markItemsAsReadServiceWithErrorHandling(
        tableView,
        conversationRowKeysToUpdate,
        itemIdsToUpdate,
        isReadValue,
        requestOptions
    );
}

function performConversationLevelMarkReadOperation(
    tableView: TableView,
    rowKeysToUpdate: string[],
    isReadValue: boolean,
    isExplicit: boolean,
    requestOptions: RequestOptions
) {
    const contextFolderId = getContextFolderIdForTable(tableView);
    const conversationActionContexts = rowKeysToUpdate.map(rowKey =>
        getApplyConversationActionContext(rowKey, tableView.id)
    );

    const markConversationsAsReadPromise = markConversationsAsReadService(
        conversationActionContexts,
        isReadValue,
        contextFolderId,
        requestOptions
    );

    const shouldRemoveRowsFromTombstone = shouldRemoveRowOnMarkReadAction(
        tableView,
        isReadValue,
        isExplicit
    );
    return processApplyConversationActionResponseMessage(
        markConversationsAsReadPromise,
        rowKeysToUpdate,
        contextFolderId,
        tableView,
        shouldRemoveRowsFromTombstone
    );
}
